import type { StoreApi } from 'zustand';
import { useTabViewStateStore } from './tabViewStateStore';
import { clearAllTabScopedEntries } from './tab/tabScopedStore';
import { dedupeTabsById } from '../utils/layoutTabUtils';
import type {
  LayoutState,
  MainContentTab,
  OpenMainContentTabInput,
  GlobalNavigationState,
  SidebarTab,
  Tab,
  TabPageEntry,
} from './layoutStore';
import { resetMainContentTabsForBoothHandoff as applyBoothHandoffMainContentLayout } from '../lib/booth/booth-main-content-layout-reset';
import { pruneTabSectionsToTabIds } from './pruneTabSections';
import { TAB_SECTION_SCOPES } from './tabSectionScopes';
import {
  applyCloseTargetSpecificTabs,
  createTargetTabWorkspaceFromTabs,
} from './layoutTargetWorkspace';

export const MAX_GLOBAL_NAV_ENTRIES = 200;

export function trimGlobalNavigationToMaxEntries({
  globalNavigation,
  maxEntries,
}: {
  globalNavigation: GlobalNavigationState;
  maxEntries: number;
}): GlobalNavigationState {
  const { entries, currentIndex } = globalNavigation;

  if (entries.length <= maxEntries) {
    return globalNavigation;
  }

  const dropCount = entries.length - maxEntries;
  const nextEntries = entries.slice(dropCount);
  const shiftedIndex = currentIndex - dropCount;

  if (nextEntries.length === 0) {
    return { entries: [], currentIndex: -1 };
  }

  const nextCurrentIndex = Math.min(
    Math.max(0, shiftedIndex),
    nextEntries.length - 1,
  );

  return { entries: nextEntries, currentIndex: nextCurrentIndex };
}

type LayoutSetState = StoreApi<LayoutState>['setState'];
type LayoutGetState = StoreApi<LayoutState>['getState'];

type LayoutTabsSlice = Pick<
  LayoutState,
  | 'mainContentTabs'
  | 'activeMainContentTabId'
  | 'tabHistory'
  | 'tabNavigationByTabId'
  | 'globalNavigation'
  | 'savedPinnedTabs'
  | 'openMainContentTab'
  | 'navigateInActiveMainContentTab'
  | 'openMainContentTabInNewInstance'
  | 'focusMainContentTabForOpenInput'
  | 'openOrFocusMainContentTab'
  | 'goBackInActiveMainContentTab'
  | 'goForwardInActiveMainContentTab'
  | 'goBackInGlobalHistory'
  | 'goForwardInGlobalHistory'
  | 'updateMainContentTab'
  | 'closeMainContentTab'
  | 'setActiveMainContentTab'
  | 'togglePinTab'
  | 'reorderMainContentTabs'
  | 'closeTargetSpecificTabs'
  | 'savePinnedTabsForTarget'
  | 'restorePinnedTabsForTarget'
  | 'resetMainContentTabsForBoothHandoff'
>;

type CreateLayoutTabsSliceParams = {
  set: LayoutSetState;
  get: LayoutGetState;
  applicationSpecificTabTypes: MainContentTab[];
  getSidebarTabForMainContentTab: (
    tabType: MainContentTab,
  ) => SidebarTab | null;
  createTabPageEntryFromOpenInput: ({
    tab,
  }: {
    tab: OpenMainContentTabInput;
  }) => TabPageEntry;
  createTabPageEntryFromTab: ({ tab }: { tab: Tab }) => TabPageEntry;
  applyPageEntryToTab: ({
    tab,
    pageEntry,
  }: {
    tab: Tab;
    pageEntry: TabPageEntry;
  }) => Tab;
  getNewTabInsertIndex: ({
    tabs,
    activeTabId,
  }: {
    tabs: Tab[];
    activeTabId: string | undefined;
  }) => number;
  generateTabId: () => string;
  onMainContentTabClosed?: ({ closingTab }: { closingTab: Tab }) => void;
};

const serializePageEntryData = ({
  value,
}: {
  value: TabPageEntry['data'];
}): string => {
  if (value === undefined) {
    return '';
  }

  return JSON.stringify(value);
};

const isSamePageEntry = ({
  left,
  right,
}: {
  left: TabPageEntry;
  right: TabPageEntry;
}): boolean => {
  return (
    left.pageId === right.pageId &&
    left.type === right.type &&
    serializePageEntryData({ value: left.data }) ===
      serializePageEntryData({ value: right.data })
  );
};

const matchesRequestedPageIdentity = ({
  entry,
  requested,
}: {
  entry: TabPageEntry;
  requested: TabPageEntry;
}): boolean => {
  if (isSamePageEntry({ left: entry, right: requested })) {
    return true;
  }

  return entry.type === requested.type && entry.pageId === requested.pageId;
};

const recordPortalVisit = ({
  globalNavigation,
  tabId,
  pageEntry,
}: {
  globalNavigation: GlobalNavigationState;
  tabId: string;
  pageEntry: TabPageEntry;
}): GlobalNavigationState => {
  const nextEntriesBase = globalNavigation.entries.slice(
    0,
    globalNavigation.currentIndex + 1,
  );
  const previousEntry = nextEntriesBase[nextEntriesBase.length - 1];

  if (
    previousEntry &&
    previousEntry.tabId === tabId &&
    isSamePageEntry({ left: previousEntry.pageEntry, right: pageEntry })
  ) {
    return trimGlobalNavigationToMaxEntries({
      globalNavigation: {
        entries: nextEntriesBase,
        currentIndex: nextEntriesBase.length - 1,
      },
      maxEntries: MAX_GLOBAL_NAV_ENTRIES,
    });
  }

  return trimGlobalNavigationToMaxEntries({
    globalNavigation: {
      entries: [...nextEntriesBase, { tabId, pageEntry }],
      currentIndex: nextEntriesBase.length,
    },
    maxEntries: MAX_GLOBAL_NAV_ENTRIES,
  });
};

const alignGlobalNavigationToTabPage = ({
  globalNavigation,
  tabId,
  pageEntry,
}: {
  globalNavigation: GlobalNavigationState;
  tabId: string;
  pageEntry: TabPageEntry;
}): GlobalNavigationState => {
  for (let index = globalNavigation.currentIndex; index >= 0; index -= 1) {
    const entry = globalNavigation.entries[index];

    if (
      entry &&
      entry.tabId === tabId &&
      isSamePageEntry({ left: entry.pageEntry, right: pageEntry })
    ) {
      return {
        ...globalNavigation,
        currentIndex: index,
      };
    }
  }

  return globalNavigation;
};

export const createLayoutTabsSlice = ({
  set,
  get,
  applicationSpecificTabTypes,
  getSidebarTabForMainContentTab,
  createTabPageEntryFromOpenInput,
  createTabPageEntryFromTab,
  applyPageEntryToTab,
  getNewTabInsertIndex,
  generateTabId,
  onMainContentTabClosed,
}: CreateLayoutTabsSliceParams): LayoutTabsSlice => ({
  mainContentTabs: [],
  activeMainContentTabId: undefined,
  tabHistory: [],
  tabNavigationByTabId: {},
  globalNavigation: {
    entries: [],
    currentIndex: -1,
  },
  savedPinnedTabs: {},

  openMainContentTab: (newTab) => {
    const { setSidebarTab } = get();

    const sidebarTab = getSidebarTabForMainContentTab(newTab.type);

    if (sidebarTab) {
      setSidebarTab(sidebarTab);
    }

    const {
      mainContentTabs,
      tabHistory,
      tabNavigationByTabId,
      globalNavigation,
    } = get();

    const existingTab = mainContentTabs.find((tab) => tab.id === newTab.id);

    const newHistory = [
      ...tabHistory.filter((id) => id !== newTab.id),
      newTab.id,
    ];

    if (existingTab) {
      const requestedPageEntry = createTabPageEntryFromOpenInput({
        tab: newTab,
      });
      const existingNavigationState = tabNavigationByTabId[newTab.id];
      const currentPageEntry =
        existingNavigationState?.entries[
          existingNavigationState.currentIndex
        ] ?? createTabPageEntryFromTab({ tab: existingTab });

      if (
        !isSamePageEntry({
          left: currentPageEntry,
          right: requestedPageEntry,
        })
      ) {
        get().openMainContentTabInNewInstance(newTab);

        return;
      }

      const nextTabNavigationByTabId =
        existingNavigationState !== undefined
          ? tabNavigationByTabId
          : {
              ...tabNavigationByTabId,
              [newTab.id]: {
                entries: [createTabPageEntryFromTab({ tab: existingTab })],
                currentIndex: 0,
              },
            };
      const activePageEntry =
        existingNavigationState?.entries[
          existingNavigationState.currentIndex
        ] ?? createTabPageEntryFromTab({ tab: existingTab });

      set({
        mainContentTabs: mainContentTabs.map((tab) => ({
          ...tab,
          isActive: tab.id === newTab.id,
        })),
        activeMainContentTabId: newTab.id,
        tabHistory: newHistory,
        tabNavigationByTabId: nextTabNavigationByTabId,
        globalNavigation: recordPortalVisit({
          globalNavigation,
          tabId: newTab.id,
          pageEntry: activePageEntry,
        }),
      });
    } else {
      const { activeMainContentTabId } = get();
      const deactivatedTabs = mainContentTabs.map((tab) => ({
        ...tab,
        isActive: false,
      }));

      const insertIndex = getNewTabInsertIndex({
        tabs: deactivatedTabs,
        activeTabId: activeMainContentTabId,
      });

      const activatedNewTab = { ...newTab, isActive: true } as Tab;
      const newTabs: Tab[] = [
        ...deactivatedTabs.slice(0, insertIndex),
        activatedNewTab,
        ...deactivatedTabs.slice(insertIndex),
      ];

      set({
        mainContentTabs: newTabs,
        activeMainContentTabId: newTab.id,
        tabHistory: newHistory,
        tabNavigationByTabId: {
          ...tabNavigationByTabId,
          [newTab.id]: {
            entries: [createTabPageEntryFromOpenInput({ tab: newTab })],
            currentIndex: 0,
          },
        },
        globalNavigation: recordPortalVisit({
          globalNavigation,
          tabId: newTab.id,
          pageEntry: createTabPageEntryFromOpenInput({ tab: newTab }),
        }),
      });
    }
  },

  navigateInActiveMainContentTab: (nextPage) => {
    const currentWindowEvent =
      typeof window !== 'undefined' ? window.event : undefined;

    if (
      currentWindowEvent instanceof MouseEvent &&
      (currentWindowEvent.metaKey || currentWindowEvent.ctrlKey)
    ) {
      get().openMainContentTabInNewInstance(nextPage);

      return;
    }

    const {
      activeMainContentTabId,
      mainContentTabs,
      tabNavigationByTabId,
      tabHistory,
      globalNavigation,
      setSidebarTab,
    } = get();
    const activeTabId = activeMainContentTabId;

    if (!activeTabId) {
      get().openMainContentTab(nextPage);

      return;
    }

    const activeTab = mainContentTabs.find((tab) => tab.id === activeTabId);

    if (!activeTab) {
      get().openMainContentTab(nextPage);

      return;
    }

    const sidebarTab = getSidebarTabForMainContentTab(nextPage.type);

    if (sidebarTab) {
      setSidebarTab(sidebarTab);
    }

    const previousNavigationState = tabNavigationByTabId[activeTabId] ?? {
      entries: [createTabPageEntryFromTab({ tab: activeTab })],
      currentIndex: 0,
    };
    const baseEntries = previousNavigationState.entries.slice(
      0,
      previousNavigationState.currentIndex + 1,
    );
    const nextPageEntry = createTabPageEntryFromOpenInput({
      tab: nextPage,
    });
    const nextEntries = [...baseEntries, nextPageEntry];
    const nextIndex = nextEntries.length - 1;
    const nextActiveTab = applyPageEntryToTab({
      tab: activeTab,
      pageEntry: nextPageEntry,
    });
    const nextHistory = [
      ...tabHistory.filter((id) => id !== activeTabId),
      activeTabId,
    ];

    set({
      mainContentTabs: mainContentTabs.map((tab) =>
        tab.id === activeTabId
          ? ({ ...nextActiveTab, isActive: true } as Tab)
          : { ...tab, isActive: false },
      ),
      activeMainContentTabId: activeTabId,
      tabHistory: nextHistory,
      tabNavigationByTabId: {
        ...tabNavigationByTabId,
        [activeTabId]: {
          entries: nextEntries,
          currentIndex: nextIndex,
        },
      },
      globalNavigation: recordPortalVisit({
        globalNavigation,
        tabId: activeTabId,
        pageEntry: nextPageEntry,
      }),
    });
  },

  openMainContentTabInNewInstance: (newTab) => {
    const {
      mainContentTabs,
      activeMainContentTabId,
      tabHistory,
      globalNavigation,
    } = get();
    const tabNavigationByTabId = get().tabNavigationByTabId;
    const tabId = generateTabId();
    const tabToInsert = { ...newTab, id: tabId };
    const initialPageEntry = createTabPageEntryFromOpenInput({ tab: newTab });
    const deactivatedTabs = mainContentTabs.map((tab) => ({
      ...tab,
      isActive: false,
    }));
    const insertIndex = getNewTabInsertIndex({
      tabs: deactivatedTabs,
      activeTabId: activeMainContentTabId,
    });
    const activatedNewTab = { ...tabToInsert, isActive: true } as Tab;
    const nextTabs: Tab[] = [
      ...deactivatedTabs.slice(0, insertIndex),
      activatedNewTab,
      ...deactivatedTabs.slice(insertIndex),
    ];
    const nextHistory = [...tabHistory.filter((id) => id !== tabId), tabId];

    set({
      mainContentTabs: nextTabs,
      activeMainContentTabId: tabId,
      tabHistory: nextHistory,
      tabNavigationByTabId: {
        ...tabNavigationByTabId,
        [tabId]: {
          entries: [initialPageEntry],
          currentIndex: 0,
        },
      },
      globalNavigation: recordPortalVisit({
        globalNavigation,
        tabId,
        pageEntry: initialPageEntry,
      }),
    });

    const sidebarTab = getSidebarTabForMainContentTab(tabToInsert.type);

    if (sidebarTab) {
      get().setSidebarTab(sidebarTab);
    }

    return tabId;
  },

  focusMainContentTabForOpenInput: (newTab) => {
    const {
      mainContentTabs,
      tabHistory,
      tabNavigationByTabId,
      globalNavigation,
    } = get();
    const requestedPageEntry = createTabPageEntryFromOpenInput({ tab: newTab });
    const sidebarTab = getSidebarTabForMainContentTab(newTab.type);

    if (sidebarTab) {
      get().setSidebarTab(sidebarTab);
    }

    type TabPageMatch = {
      tabId: string;
      pageIndex: number;
      pageEntry: TabPageEntry;
    };

    const matches: TabPageMatch[] = [];

    for (const tab of mainContentTabs) {
      const navigation = tabNavigationByTabId[tab.id];
      const entries = navigation?.entries ?? [
        createTabPageEntryFromTab({ tab }),
      ];
      const currentIndex = navigation?.currentIndex ?? 0;

      for (let pageIndex = 0; pageIndex < entries.length; pageIndex += 1) {
        const entry = entries[pageIndex];

        if (
          matchesRequestedPageIdentity({
            entry,
            requested: requestedPageEntry,
          })
        ) {
          matches.push({ tabId: tab.id, pageIndex, pageEntry: entry });
        }
      }

      const currentEntry = entries[currentIndex] ?? entries[0];

      if (
        currentEntry &&
        matchesRequestedPageIdentity({
          entry: currentEntry,
          requested: requestedPageEntry,
        }) &&
        !matches.some((match) => match.tabId === tab.id)
      ) {
        matches.push({
          tabId: tab.id,
          pageIndex: currentIndex,
          pageEntry: currentEntry,
        });
      }
    }

    if (matches.length === 0) {
      return false;
    }

    const bestMatch = matches.sort((left, right) => {
      const leftHistoryIndex = tabHistory.lastIndexOf(left.tabId);
      const rightHistoryIndex = tabHistory.lastIndexOf(right.tabId);

      return rightHistoryIndex - leftHistoryIndex;
    })[0];

    const tab = mainContentTabs.find(
      (candidate) => candidate.id === bestMatch.tabId,
    );

    if (!tab) {
      return false;
    }

    const navigation = tabNavigationByTabId[bestMatch.tabId];
    const entries = navigation?.entries ?? [createTabPageEntryFromTab({ tab })];
    const targetPageEntry = isSamePageEntry({
      left: bestMatch.pageEntry,
      right: requestedPageEntry,
    })
      ? bestMatch.pageEntry
      : requestedPageEntry;
    const nextEntries = [...entries];

    if (bestMatch.pageIndex < nextEntries.length) {
      nextEntries[bestMatch.pageIndex] = targetPageEntry;
    }

    const nextActiveTab = applyPageEntryToTab({
      tab,
      pageEntry: targetPageEntry,
    });
    const nextHistory = [
      ...tabHistory.filter((id) => id !== bestMatch.tabId),
      bestMatch.tabId,
    ];

    set({
      mainContentTabs: mainContentTabs.map((candidate) =>
        candidate.id === bestMatch.tabId
          ? ({ ...nextActiveTab, isActive: true } as Tab)
          : { ...candidate, isActive: false },
      ),
      activeMainContentTabId: bestMatch.tabId,
      tabHistory: nextHistory,
      tabNavigationByTabId: {
        ...tabNavigationByTabId,
        [bestMatch.tabId]: {
          entries: nextEntries,
          currentIndex: bestMatch.pageIndex,
        },
      },
      globalNavigation: recordPortalVisit({
        globalNavigation,
        tabId: bestMatch.tabId,
        pageEntry: targetPageEntry,
      }),
    });

    return true;
  },

  openOrFocusMainContentTab: (newTab) => {
    if (get().focusMainContentTabForOpenInput(newTab)) {
      return;
    }

    get().openMainContentTab(newTab);
  },

  goBackInActiveMainContentTab: () => {
    const {
      activeMainContentTabId,
      mainContentTabs,
      tabNavigationByTabId,
      globalNavigation,
    } = get();
    const tabId = activeMainContentTabId;

    if (!tabId) return;
    const tab = mainContentTabs.find(
      (mainContentTab) => mainContentTab.id === tabId,
    );

    if (!tab) return;
    const navigationState = tabNavigationByTabId[tabId];

    if (!navigationState || navigationState.currentIndex <= 0) return;

    const nextIndex = navigationState.currentIndex - 1;
    const nextEntry = navigationState.entries[nextIndex];

    if (!nextEntry) return;

    const nextTab = applyPageEntryToTab({ tab, pageEntry: nextEntry });
    const sidebarTab = getSidebarTabForMainContentTab(nextEntry.type);

    if (sidebarTab) {
      get().setSidebarTab(sidebarTab);
    }

    set({
      mainContentTabs: mainContentTabs.map((mainContentTab) =>
        mainContentTab.id === tabId ? nextTab : mainContentTab,
      ),
      tabNavigationByTabId: {
        ...tabNavigationByTabId,
        [tabId]: {
          ...navigationState,
          currentIndex: nextIndex,
        },
      },
      globalNavigation: recordPortalVisit({
        globalNavigation,
        tabId,
        pageEntry: nextEntry,
      }),
    });
  },

  goForwardInActiveMainContentTab: () => {
    const {
      activeMainContentTabId,
      mainContentTabs,
      tabNavigationByTabId,
      globalNavigation,
    } = get();
    const tabId = activeMainContentTabId;

    if (!tabId) return;
    const tab = mainContentTabs.find(
      (mainContentTab) => mainContentTab.id === tabId,
    );

    if (!tab) return;
    const navigationState = tabNavigationByTabId[tabId];

    if (!navigationState) return;

    const nextIndex = navigationState.currentIndex + 1;

    if (nextIndex >= navigationState.entries.length) return;
    const nextEntry = navigationState.entries[nextIndex];

    if (!nextEntry) return;

    const nextTab = applyPageEntryToTab({ tab, pageEntry: nextEntry });
    const sidebarTab = getSidebarTabForMainContentTab(nextEntry.type);

    if (sidebarTab) {
      get().setSidebarTab(sidebarTab);
    }

    set({
      mainContentTabs: mainContentTabs.map((mainContentTab) =>
        mainContentTab.id === tabId ? nextTab : mainContentTab,
      ),
      tabNavigationByTabId: {
        ...tabNavigationByTabId,
        [tabId]: {
          ...navigationState,
          currentIndex: nextIndex,
        },
      },
      globalNavigation: recordPortalVisit({
        globalNavigation,
        tabId,
        pageEntry: nextEntry,
      }),
    });
  },

  goBackInGlobalHistory: () => {
    const {
      globalNavigation,
      mainContentTabs,
      tabNavigationByTabId,
      tabHistory,
    } = get();
    const existingTabsById = new Map(
      mainContentTabs.map((mainContentTab) => [
        mainContentTab.id,
        mainContentTab,
      ]),
    );

    for (
      let targetIndex = globalNavigation.currentIndex - 1;
      targetIndex >= 0;
      targetIndex -= 1
    ) {
      const targetVisit = globalNavigation.entries[targetIndex];
      const targetTab = existingTabsById.get(targetVisit?.tabId ?? '');

      if (!targetVisit || !targetTab) {
        continue;
      }

      const updatedTargetTab = applyPageEntryToTab({
        tab: targetTab,
        pageEntry: targetVisit.pageEntry,
      });
      const sidebarTab = getSidebarTabForMainContentTab(
        targetVisit.pageEntry.type,
      );

      if (sidebarTab) {
        get().setSidebarTab(sidebarTab);
      }

      const targetTabNavigation = tabNavigationByTabId[targetVisit.tabId];
      const matchingEntryIndex = targetTabNavigation?.entries.findIndex(
        (entry) =>
          isSamePageEntry({ left: entry, right: targetVisit.pageEntry }),
      );
      const nextTabNavigationByTabId =
        targetTabNavigation &&
        matchingEntryIndex !== undefined &&
        matchingEntryIndex >= 0
          ? {
              ...tabNavigationByTabId,
              [targetVisit.tabId]: {
                ...targetTabNavigation,
                currentIndex: matchingEntryIndex,
              },
            }
          : {
              ...tabNavigationByTabId,
              [targetVisit.tabId]: {
                entries: [targetVisit.pageEntry],
                currentIndex: 0,
              },
            };
      const nextHistory = [
        ...tabHistory.filter(
          (historyTabId) => historyTabId !== targetVisit.tabId,
        ),
        targetVisit.tabId,
      ];

      set({
        mainContentTabs: mainContentTabs.map((mainContentTab) =>
          mainContentTab.id === targetVisit.tabId
            ? { ...updatedTargetTab, isActive: true }
            : { ...mainContentTab, isActive: false },
        ),
        activeMainContentTabId: targetVisit.tabId,
        tabHistory: nextHistory,
        tabNavigationByTabId: nextTabNavigationByTabId,
        globalNavigation: {
          ...globalNavigation,
          currentIndex: targetIndex,
        },
      });

      return;
    }
  },

  goForwardInGlobalHistory: () => {
    const {
      globalNavigation,
      mainContentTabs,
      tabNavigationByTabId,
      tabHistory,
    } = get();
    const existingTabsById = new Map(
      mainContentTabs.map((mainContentTab) => [
        mainContentTab.id,
        mainContentTab,
      ]),
    );

    for (
      let targetIndex = globalNavigation.currentIndex + 1;
      targetIndex < globalNavigation.entries.length;
      targetIndex += 1
    ) {
      const targetVisit = globalNavigation.entries[targetIndex];
      const targetTab = existingTabsById.get(targetVisit?.tabId ?? '');

      if (!targetVisit || !targetTab) {
        continue;
      }

      const updatedTargetTab = applyPageEntryToTab({
        tab: targetTab,
        pageEntry: targetVisit.pageEntry,
      });
      const sidebarTab = getSidebarTabForMainContentTab(
        targetVisit.pageEntry.type,
      );

      if (sidebarTab) {
        get().setSidebarTab(sidebarTab);
      }

      const targetTabNavigation = tabNavigationByTabId[targetVisit.tabId];
      const matchingEntryIndex = targetTabNavigation?.entries.findIndex(
        (entry) =>
          isSamePageEntry({ left: entry, right: targetVisit.pageEntry }),
      );
      const nextTabNavigationByTabId =
        targetTabNavigation &&
        matchingEntryIndex !== undefined &&
        matchingEntryIndex >= 0
          ? {
              ...tabNavigationByTabId,
              [targetVisit.tabId]: {
                ...targetTabNavigation,
                currentIndex: matchingEntryIndex,
              },
            }
          : {
              ...tabNavigationByTabId,
              [targetVisit.tabId]: {
                entries: [targetVisit.pageEntry],
                currentIndex: 0,
              },
            };
      const nextHistory = [
        ...tabHistory.filter(
          (historyTabId) => historyTabId !== targetVisit.tabId,
        ),
        targetVisit.tabId,
      ];

      set({
        mainContentTabs: mainContentTabs.map((mainContentTab) =>
          mainContentTab.id === targetVisit.tabId
            ? { ...updatedTargetTab, isActive: true }
            : { ...mainContentTab, isActive: false },
        ),
        activeMainContentTabId: targetVisit.tabId,
        tabHistory: nextHistory,
        tabNavigationByTabId: nextTabNavigationByTabId,
        globalNavigation: {
          ...globalNavigation,
          currentIndex: targetIndex,
        },
      });

      return;
    }
  },

  updateMainContentTab: (tabId, updates) => {
    const { mainContentTabs, tabNavigationByTabId } = get();

    if (mainContentTabs.findIndex((tab) => tab.id === tabId) === -1) return;
    const currentNavigationState = tabNavigationByTabId[tabId];
    const nextTabNavigationByTabId =
      currentNavigationState && updates.title
        ? {
            ...tabNavigationByTabId,
            [tabId]: {
              ...currentNavigationState,
              entries: currentNavigationState.entries.map((entry, index) =>
                index === currentNavigationState.currentIndex
                  ? { ...entry, title: updates.title ?? entry.title }
                  : entry,
              ),
            },
          }
        : tabNavigationByTabId;

    set({
      mainContentTabs: mainContentTabs.map((tab) =>
        tab.id === tabId ? { ...tab, ...updates } : tab,
      ),
      tabNavigationByTabId: nextTabNavigationByTabId,
    });
  },

  closeMainContentTab: (tabId) => {
    const {
      mainContentTabs,
      activeMainContentTabId,
      tabHistory,
      tabGroups,
      tabNavigationByTabId,
      globalNavigation,
    } = get();
    const closingTab = mainContentTabs.find((tab) => tab.id === tabId);
    const updatedTabs = mainContentTabs.filter((tab) => tab.id !== tabId);

    useTabViewStateStore.getState().clearTabState(tabId);
    clearAllTabScopedEntries(tabId);
    if (closingTab) {
      onMainContentTabClosed?.({ closingTab });
    }

    const newHistory = tabHistory.filter((id) => id !== tabId);
    const { [tabId]: _removedNavigationState, ...remainingNavigationStates } =
      tabNavigationByTabId;

    let updatedGroups = tabGroups;

    if (closingTab?.groupId) {
      updatedGroups = tabGroups
        .map((group) =>
          group.id === closingTab.groupId
            ? {
                ...group,
                tabIds: group.tabIds.filter((id) => id !== tabId),
              }
            : group,
        )
        .filter((group) => group.tabIds.length > 0);
    }

    if (updatedTabs.length === 0) {
      const welcomeTab = {
        id: 'welcome',
        title: 'Welcome',
        type: 'welcome' as const,
        isActive: true,
      };

      set({
        mainContentTabs: [welcomeTab],
        activeMainContentTabId: 'welcome',
        tabHistory: ['welcome'],
        tabGroups: updatedGroups,
        tabNavigationByTabId: {
          ...remainingNavigationStates,
          welcome: {
            entries: [createTabPageEntryFromTab({ tab: welcomeTab })],
            currentIndex: 0,
          },
        },
      });

      return;
    }

    if (tabId === activeMainContentTabId && updatedTabs.length > 0) {
      const remainingTabIds = new Set(updatedTabs.map((tab) => tab.id));
      let newActiveTabId: string | undefined;

      for (let i = newHistory.length - 1; i >= 0; i--) {
        if (remainingTabIds.has(newHistory[i])) {
          newActiveTabId = newHistory[i];
          break;
        }
      }

      if (!newActiveTabId) {
        newActiveTabId = updatedTabs[updatedTabs.length - 1].id;
      }

      const { setSidebarTab } = get();
      const newActiveTab = updatedTabs.find((tab) => tab.id === newActiveTabId);

      if (newActiveTab) {
        const sidebarTab = getSidebarTabForMainContentTab(newActiveTab.type);

        if (sidebarTab) {
          setSidebarTab(sidebarTab);
        }
      }

      const newActiveTabNavigation = newActiveTabId
        ? remainingNavigationStates[newActiveTabId]
        : undefined;
      const newActivePageEntry =
        newActiveTabNavigation?.entries[newActiveTabNavigation.currentIndex] ??
        (newActiveTab
          ? createTabPageEntryFromTab({ tab: newActiveTab })
          : undefined);
      const nextGlobalNavigation =
        newActiveTabId && newActivePageEntry
          ? recordPortalVisit({
              globalNavigation: alignGlobalNavigationToTabPage({
                globalNavigation,
                tabId: newActiveTabId,
                pageEntry: newActivePageEntry,
              }),
              tabId: newActiveTabId,
              pageEntry: newActivePageEntry,
            })
          : globalNavigation;

      set({
        mainContentTabs: updatedTabs.map((tab) => ({
          ...tab,
          isActive: tab.id === newActiveTabId,
        })),
        activeMainContentTabId: newActiveTabId,
        tabHistory: newHistory,
        tabGroups: updatedGroups,
        tabNavigationByTabId: remainingNavigationStates,
        globalNavigation: nextGlobalNavigation,
      });
    } else {
      set({
        mainContentTabs: updatedTabs,
        tabHistory: newHistory,
        tabGroups: updatedGroups,
        tabNavigationByTabId: remainingNavigationStates,
      });
    }
  },

  setActiveMainContentTab: (tabId) => {
    const {
      mainContentTabs,
      tabHistory,
      tabNavigationByTabId,
      globalNavigation,
      setSidebarTab,
    } = get();

    const activatedTab = mainContentTabs.find((tab) => tab.id === tabId);

    if (activatedTab) {
      const sidebarTab = getSidebarTabForMainContentTab(activatedTab.type);

      if (sidebarTab) {
        setSidebarTab(sidebarTab);
      }
    }

    const newHistory = [...tabHistory.filter((id) => id !== tabId), tabId];
    const activePageEntry =
      activatedTab && tabNavigationByTabId[tabId]
        ? tabNavigationByTabId[tabId].entries[
            tabNavigationByTabId[tabId].currentIndex
          ]
        : activatedTab
          ? createTabPageEntryFromTab({ tab: activatedTab })
          : undefined;

    set({
      mainContentTabs: mainContentTabs.map((tab) => ({
        ...tab,
        isActive: tab.id === tabId,
      })),
      activeMainContentTabId: tabId,
      tabHistory: newHistory,
      ...(activePageEntry && {
        globalNavigation: recordPortalVisit({
          globalNavigation,
          tabId,
          pageEntry: activePageEntry,
        }),
      }),
    });
  },

  togglePinTab: (tabId) => {
    const { mainContentTabs } = get();
    const tab = mainContentTabs.find((existingTab) => existingTab.id === tabId);

    if (!tab) return;

    const updatedTabs = mainContentTabs.map((existingTab) =>
      existingTab.id === tabId
        ? { ...existingTab, isPinned: !existingTab.isPinned }
        : existingTab,
    );

    const pinnedTabs = updatedTabs.filter(
      (existingTab) => existingTab.isPinned,
    );
    const unpinnedTabs = updatedTabs.filter(
      (existingTab) => !existingTab.isPinned,
    );

    set({
      mainContentTabs: [...pinnedTabs, ...unpinnedTabs],
    });
  },

  reorderMainContentTabs: (fromIndex, toIndex) => {
    const { mainContentTabs } = get();
    const newTabs = [...mainContentTabs];
    const [movedTab] = newTabs.splice(fromIndex, 1);

    newTabs.splice(toIndex, 0, movedTab);

    set({
      mainContentTabs: newTabs,
    });
  },

  closeTargetSpecificTabs: (options) => {
    const state = get();
    const patch = applyCloseTargetSpecificTabs({
      state,
      options,
      applicationSpecificTabTypes,
      getSidebarTabForMainContentTab,
    });

    if (!patch) {
      return;
    }

    const finalTabIds = new Set(
      (patch.mainContentTabs ?? state.mainContentTabs).map((tab) => tab.id),
    );
    const {
      [TAB_SECTION_SCOPES.applicationSettings]: _removedApplicationSettings,
      ...survivingScopes
    } = state.tabSections;

    set({
      ...patch,
      endpointTabs: {},
      webPageTabs: {},
      tabSections: pruneTabSectionsToTabIds({
        tabSections: survivingScopes,
        tabIds: finalTabIds,
      }),
    });
  },

  savePinnedTabsForTarget: (targetScopeKey) => {
    const { mainContentTabs, savedPinnedTabs } = get();

    const pinnedAppTabs = dedupeTabsById({
      tabs: mainContentTabs.filter(
        (tab) => tab.isPinned && applicationSpecificTabTypes.includes(tab.type),
      ),
    });

    if (pinnedAppTabs.length > 0) {
      set({
        savedPinnedTabs: {
          ...savedPinnedTabs,
          [targetScopeKey]: createTargetTabWorkspaceFromTabs({
            tabs: pinnedAppTabs,
          }),
        },
      });
    }
  },

  restorePinnedTabsForTarget: (targetScopeKey) => {
    const {
      mainContentTabs,
      savedPinnedTabs,
      tabHistory,
      tabNavigationByTabId,
    } = get();

    const workspace = savedPinnedTabs[targetScopeKey];
    const tabsToRestore = workspace
      ? dedupeTabsById({ tabs: workspace.tabs })
      : undefined;

    if (!tabsToRestore || tabsToRestore.length === 0) return;

    const existingTabIds = new Set(mainContentTabs.map((tab) => tab.id));
    const newTabsToAdd = tabsToRestore.filter(
      (tab) => !existingTabIds.has(tab.id),
    );

    if (newTabsToAdd.length === 0) return;

    const existingPinnedTabs = mainContentTabs.filter((tab) => tab.isPinned);
    const existingUnpinnedTabs = mainContentTabs.filter((tab) => !tab.isPinned);

    const restoredTabs = newTabsToAdd.map((tab) => ({
      ...tab,
      isActive: false,
      isPinned: true,
    }));
    const restoredTabIds = new Set(restoredTabs.map((tab) => tab.id));
    const restoredNavigation = Object.fromEntries(
      Object.entries(workspace.tabNavigationByTabId).filter(([tabId]) =>
        restoredTabIds.has(tabId),
      ),
    );

    set({
      mainContentTabs: [
        ...existingPinnedTabs,
        ...restoredTabs,
        ...existingUnpinnedTabs,
      ],
      tabHistory: [...newTabsToAdd.map((tab) => tab.id), ...tabHistory],
      tabNavigationByTabId: {
        ...tabNavigationByTabId,
        ...Object.fromEntries(
          restoredTabs.map((tab) => [
            tab.id,
            restoredNavigation[tab.id] ?? {
              entries: [createTabPageEntryFromTab({ tab })],
              currentIndex: 0,
            },
          ]),
        ),
      },
    });
  },

  resetMainContentTabsForBoothHandoff: (homeTab) => {
    applyBoothHandoffMainContentLayout({
      get,
      set,
      homeTab,
      createTabPageEntryFromOpenInput,
      getSidebarTabForMainContentTab,
      onMainContentTabClosed,
    });
  },
});
